import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * The filter popup.
 *
 * WHAT IT REPLACES
 *
 * Every list screen had its own copy of the same block: a `Transition`
 * wrapping an absolutely-positioned panel pinned to `right-5`, a header with
 * Reset and Close, and a two-column grid of selects. Twenty-nine copies, which
 * had drifted into six different class strings and three different orderings
 * of the same header buttons.
 *
 * WHY A DIALOG AND NOT A DROPDOWN
 *
 * The old panel was anchored to the page, not to the button that opened it, so
 * it landed in a different place on every screen and could open off-canvas on
 * a phone. It also drew over the table without trapping focus, so tabbing out
 * of it left you somewhere behind it with the panel still showing.
 *
 * A dialog fixes all three: one position, a backdrop that says the rest of the
 * page is inert, focus kept inside, and Escape closes.
 *
 * FIELDS ARE THE CALLER'S
 *
 * `children` are the filter controls, laid out in the two-column grid the
 * screens already used. This component owns the frame, the header and the
 * actions; it makes no assumption about what is being filtered.
 */
const FilterPanel = ({
  open,
  onClose,
  onReset,
  onApply,
  title = 'Filters',
  applyLabel = 'Apply',
  resetLabel = 'Reset',
  columns = 2,
  children
}) => (
  <Transition show={!!open} as={Fragment}>
    <Dialog onClose={onClose} className="relative z-50">
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      </Transition.Child>

      <div className="fixed inset-0 overflow-y-auto p-4 flex items-center justify-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full sm:max-w-[600px] lg:max-w-[700px] rounded-card bg-white dark:bg-shoorah-darkBgTabColor dark:text-white border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder shadow-xl">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-shoorah-borderGray dark:border-shoorah-darkBorder">
              <Dialog.Title className="text-base font-medium">{title}</Dialog.Title>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close filters"
                className="text-shoorah-gray3 hover:text-shoorah-lightBlack dark:hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className={`p-5 grid gap-4 ${columns === 1 ? '' : 'sm:grid-cols-2'}`}>
              {children}
            </div>

            {/*
              Actions at the bottom, where a dialog's actions belong. Reset is
              secondary and sits away from Apply so the two are not confused
              when one of them discards work.
            */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-shoorah-borderGray dark:border-shoorah-darkBorder">
              {onReset ? (
                <button type="button" className="btn-secondary btn-sm" onClick={onReset}>
                  {resetLabel}
                </button>
              ) : null}
              {onApply ? (
                <button type="button" className="btn-primary btn-sm" onClick={onApply}>
                  {applyLabel}
                </button>
              ) : (
                <button type="button" className="btn-primary btn-sm" onClick={onClose}>
                  Done
                </button>
              )}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

FilterPanel.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Omit when a screen has nothing to reset. */
  onReset: PropTypes.func,
  /** Omit when filters apply as they change; the footer then shows "Done". */
  onApply: PropTypes.func,
  title: PropTypes.string,
  applyLabel: PropTypes.string,
  resetLabel: PropTypes.string,
  columns: PropTypes.oneOf([1, 2]),
  children: PropTypes.node
};

export default FilterPanel;
